import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { IconSpinner } from '../icons/index.js'

/**
 * The button vocabulary — REBUILT 2026-08-26 against `DESIGN-HANDOVER.md` §5.1 (Figma node
 * `70:186`, 120 variants enumerated). This is a full rebuild, not an extension: every colour,
 * every size and the whole state model changed.
 *
 * Three prop names deviate from the Figma axis names, for collisions React forces on us. The
 * axis is recorded here so the mapping is not lost:
 *
 *   Figma `Style`  → prop `variant`   (`style` is HTMLAttributes.style — taken)
 *   Figma `Type`   → prop `iconOnly`  (`type` is the button's HTML type — taken)
 *   Figma `State`  → `disabled` + `loading` + CSS pseudo-classes
 *
 * WHAT CHANGED, and why a reader should not "fix" it back:
 *   - `danger` is GONE (owner decision D-BTN-1, 2026-08-25). Zero of 120 Figma variants use red.
 *     Destructive actions render as `ghost` with a danger-tinted text class at the call site.
 *     Re-adding a `danger` case here to make a call site typecheck directly contradicts D-BTN-1.
 *   - `tertiary` is a real fourth style (D-BTN-2), not an alias of ghost.
 *   - Hover goes DARKER, not lighter. Disabled LOSES colour rather than gaining opacity —
 *     so there is no `disabled:opacity-50` anywhere below.
 *   - Ghost is the one style whose disabled state stays TRANSPARENT instead of flattening to
 *     `#e6e6e6` like the other three.
 *
 * Colours resolve from Epic 52's `@theme` token layer in `apps/web/src/index.css`. Never write a
 * raw hex here, and never substitute Tailwind `indigo-600` (#4f46e5) for the action colour
 * `#3b45e6` — measured as a different value, not a rounding difference.
 */

/**
 * DARK MODE — retained by owner decision, 2026-08-26. NOT SPECIFIED BY FIGMA.
 *
 * The 2026-08-25 evidence pull contains zero dark-mode frames for this component set, and
 * `DESIGN-HANDOVER.md` §5.1 records the question as an OPEN decision (§10 Blocking #15). But dark
 * mode is a live, shipped feature here: `lib/theme.ts` toggles the `dark` class, `index.css`
 * defines `@custom-variant dark`, and 43 files under `apps/web/src` carry `dark:` utilities.
 * Dropping these would silently remove a working feature.
 *
 * So the `dark:` values below are INFERENCE, carried forward from the old contract to hold the
 * line — they are not a design spec, and `tertiary` (a style with no old counterpart) borrows
 * the neutral-surface treatment by analogy. When the dark-mode decision is actually made, this
 * block is what it replaces.
 */

type Variant = 'primary' | 'secondary' | 'tertiary' | 'ghost'
type Size = 'sm' | 'md' | 'lg'

/**
 * `cursor-pointer` lives in the BASE, not per call site: Tailwind v4 dropped the preflight rule
 * that gave `<button>` a pointer cursor, so every button read as dead text without it.
 * `disabled:cursor-not-allowed` is listed after it and therefore still wins.
 *
 * `cursor-pointer`, the `focus-visible` ring and the pressed pseudo-class are carried forward
 * from the old contract on purpose: §5.1 states they "cannot be confirmed or contradicted from
 * Figma" and should be kept until a live QA pass says otherwise.
 */
const BASE =
  'inline-flex cursor-pointer items-center justify-center gap-small rounded-150 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-surface-primary focus-visible:ring-offset-1 disabled:cursor-not-allowed'

const VARIANT: Record<Variant, string> = {
  primary: [
    'bg-surface-primary text-text-primary-on-color',
    'hover:bg-surface-primary-hover active:bg-surface-primary-pressed',
    'disabled:bg-surface-disabled disabled:text-text-disabled disabled:hover:bg-surface-disabled',
  ].join(' '),
  // No border. A pixel row-scan at the fill edge returns flat #f4fafe, zero stroke (§5.1).
  secondary: [
    'bg-surface-secondary text-text-primary',
    'hover:bg-surface-secondary-hover active:bg-surface-secondary-pressed',
    'disabled:bg-surface-disabled disabled:text-text-disabled disabled:hover:bg-surface-disabled',
    'dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:active:bg-gray-600',
  ].join(' '),
  tertiary: [
    'bg-surface-tertiary text-text-default',
    'hover:bg-surface-tertiary-hover active:bg-surface-tertiary-pressed',
    'disabled:bg-surface-disabled disabled:text-text-disabled disabled:hover:bg-surface-disabled',
    'dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:active:bg-gray-600',
  ].join(' '),
  // Ghost's hover/pressed are pixel-identical to Tertiary's normal/hover (§5.1) — that is
  // measured, not a copy-paste slip. Its disabled state stays transparent.
  ghost: [
    'bg-transparent text-text-default',
    'hover:bg-surface-tertiary active:bg-surface-tertiary-hover',
    'disabled:bg-transparent disabled:text-text-disabled disabled:hover:bg-transparent',
    'dark:text-gray-300 dark:hover:bg-gray-800 dark:active:bg-gray-700',
  ].join(' '),
}

/** Height / horizontal padding / label type, per §5.1's geometry table. Radius is 6px at every size. */
const SIZE: Record<Size, string> = {
  sm: 'h-5 px-small text-body-small-medium',
  md: 'h-6 px-small-medium text-body-default-medium',
  lg: 'h-8 px-medium text-body-default-medium',
}

/** Type=Icon is a bare square at the height's own footprint, holding one centered 16x16 icon. */
const ICON_ONLY: Record<Size, string> = {
  sm: 'h-5 w-5 p-none',
  md: 'h-6 w-6 p-none',
  lg: 'h-8 w-8 p-none',
}

export function Button({
  variant = 'secondary',
  size = 'lg',
  iconOnly = false,
  loading = false,
  className,
  children,
  disabled,
  ...rest
}: {
  variant?: Variant
  /**
   * Defaults to `lg` (32px). The old contract's default `md` rendered ~34px, so `lg` is what
   * preserves the shipped visual scale through this rebuild; the new `md` is 24px.
   */
  size?: Size
  /** Figma `Type=Icon`: a bare square footprint holding one centered 16x16 icon, no label. */
  iconOnly?: boolean
  /**
   * Figma `State=Loader`. The Icon-Text footprint collapses to the square Icon-only footprint,
   * the label is dropped, and a single centered 12x12 spinner remains. The fill is unchanged
   * from Normal — which for `ghost` means it stays transparent, since it has no fill to spin
   * against.
   */
  loading?: boolean
  children?: ReactNode
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const collapsed = iconOnly || loading
  const shape = collapsed ? ICON_ONLY[size] : SIZE[size]

  return (
    <button
      type="button"
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${BASE} ${VARIANT[variant]} ${shape}${className ? ` ${className}` : ''}`}
    >
      {loading ? <IconSpinner size={12} className="animate-spin" /> : children}
    </button>
  )
}
